import React from 'react'
import { observer } from 'mobx-react'
import ConversationViewModel from '../ConversationViewModel'
import styles from './Messages.sass'
import Welcome, { WelcomeAction } from './Welcome'
import List from './List'
import throttle from 'lodash/throttle'
import Icon from 'components/Icon'
import { mergeRefs } from '../../../../utils/react-util'

interface MessagesProps {
  vm: ConversationViewModel
  small?: boolean
  welcomeActions?: WelcomeAction[]
  scrollRef?: React.RefObject<HTMLElement | null>
}

export default observer(function Messages({
  vm,
  small,
  welcomeActions,
  scrollRef,
}: MessagesProps) {
  const fallbackRef = React.useRef<HTMLElement | null>(null)
  scrollRef = scrollRef ?? fallbackRef
  const { tailing, scrollToBottom } = useReverseScroll(
    scrollRef,
    vm.messages.length,
    vm.scroller.more
  )
  return (
    <div className={styles.root}>
      <div className={styles.scroller} ref={mergeRefs([scrollRef, scrollRef])}>
        <div className={styles.filler} />
        <div className={styles.content}>
          <Welcome welcomeActions={welcomeActions} small={small} vm={vm} />
          <List vm={vm} />
        </div>
      </div>
      {!tailing && (
        <div className={styles.scrollToBottomContainer}>
          <a
            onClick={() => scrollToBottom()}
            className={styles.scrollToBottomBtn}
          >
            <Icon auto style={{ transform: 'rotate(90deg)' }} name="arrow" />
          </a>
        </div>
      )}
    </div>
  )
})

const TOP_THRESH = 2000
const BOTTOM_THRESH = 25

/**
 * Used for automatically tailing the bottom of the scroll container.
 *
 * @param ref
 * @param messageCount
 * @param onLoadMore
 */
function useReverseScroll(
  ref: React.RefObject<HTMLElement | null>,
  messageCount: number,
  onLoadMore: () => unknown
) {
  // State for whether we are tailing.
  const [tailing, setTailing] = React.useState(true)
  const prevMsgCountRef = React.useRef(messageCount)
  const scrollTrackHeightRef = React.useRef(0)
  const shouldLoadMoreRef = React.useRef(false)
  const scrollToBottom = React.useCallback(() => {
    if (ref.current) {
      ref.current.scrollTop = ref.current.scrollHeight
    }
  }, [ref])

  const onScroll = React.useCallback(
    throttle(() => {
      const node = ref.current
      if (!node) {
        return
      }
      const { scrollTop, offsetHeight, scrollHeight } = node
      if (scrollTop > TOP_THRESH) {
        if (shouldLoadMoreRef.current) {
          shouldLoadMoreRef.current = false
          onLoadMore()
        }
      } else {
        shouldLoadMoreRef.current = true
      }

      // Whenever we scroll, determine whether to tail.
      setTailing(
        offsetHeight === scrollHeight ||
          scrollTop + offsetHeight > scrollHeight - BOTTOM_THRESH
      )
    }, 200),
    [messageCount, onLoadMore]
  )

  // Attach a scroll listener.
  React.useEffect(() => {
    if (ref.current) {
      const node = ref.current
      node.addEventListener('scroll', onScroll)
      return () => {
        node.removeEventListener('scroll', onScroll)
      }
    }
  }, [onScroll, ref])

  // Make sure we stick to the bottom when needed
  // as well as reposition scroll when loading old messages.
  React.useLayoutEffect(() => {
    const node = ref.current
    if (!node) {
      return
    }

    // When tailing, scroll to the bottom.
    if (tailing) {
      prevMsgCountRef.current = messageCount
      scrollToBottom()
    } else if (prevMsgCountRef.current < messageCount) {
      // If new messages came in and we are NOT tailing, offset the scroll.
      // This avoids jumping when loading more messages.
      node.scrollTop += node.scrollHeight - scrollTrackHeightRef.current
      prevMsgCountRef.current = messageCount
    }
  }, [scrollToBottom, messageCount, tailing, ref])

  // Any time the component rerenders with the same amount of
  // messages, capture it's scroll info. If the message count changes,
  // the above hook will use the previously captured scroll info to
  // reposition.
  React.useLayoutEffect(() => {
    if (!ref.current) {
      return
    }
    if (prevMsgCountRef.current === messageCount) {
      scrollTrackHeightRef.current = ref.current.scrollHeight
    }
  }, [messageCount])

  return { tailing, scrollToBottom }
}
